import type { Task } from "~skillBuilder/utils/types";

import { image, tip, title } from "../../contentBlocks";

export const TspStudentCenteredInstruction = {
  name: "Student-Centered Instructional Practices",
  uniqueName: "tsp-student-centered-instruction",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "the-sub-playbook.student-centered-instruction/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Student-Centered Instructional Practices", durationTimeText: "10 min" })}
<p>Student-centered instructional approaches focus on actively involving students in their own learning. This can be used as a tool to help build rapport and trust quickly.</p>
<p>By giving students voice and choice in their learning, you are showing respect for students&apos; ideas and experiences, which can lower resistance and increase cooperation. These approaches also reduce power struggles, as students are more engaged and invested when they have a role in the process.</p>
<p>As you know, it is common for subs to step into unfamiliar classrooms, so a student-centered method provides flexibility, allowing you to adapt to student needs and classroom norms without derailing the learning that has already been established. Additionally, these strategies support a wide range of learners and help create a calm, inclusive environment where students feel safe, seen, and ready to participate.</p>
<div align="center">
<div style="max-width:300px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1734092364/skillbuilderAssets/optimized/positive-enviro_divider-engaged.jpg", alt: "A male teacher and four students doing a science experiment in a science classroom." })}
</div>
</div>
`,
          },
        },
      ],
    },
    // Step 2
    {
      questions: [
        // Question 2.1
        {
          attribute: "the-sub-playbook.student-centered-instruction/differentiation",
          label: "Task and Instruction Differentiation",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>You&rsquo;ll find diverse academic levels and learning styles all within one classroom. You may encounter students with IEPs, 504 plans, or varied skill levels. Even if you&apos;re unfamiliar with individual needs, using simple strategies to vary tasks and instructions helps all learners stay engaged.</p>
<ul style="margin-left: 16px;">
  <li>🤝 Encourage peer explanation&mdash;students often learn better from each other.</li>
  <li>🤝 Break tasks into manageable chunks: &ldquo;Let&rsquo;s work together on this one, then we&rsquo;ll do it individually.&rdquo;</li>
  <li>🤝 Let students work at their own pace if time allows.</li>
</ul>
${tip({ text: "For the early finishers: Have them explain their thinking to a peer, write a summary, or create a question for the class." })}
`,
          },
        },
      ],
    },
    // Step 3
    {
      questions: [
        // Question 3.1
        {
          attribute: "the-sub-playbook.student-centered-instruction/scaffolding",
          label: "Scaffolding Lessons",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Scaffolding looks like supporting students just enough to help them complete a new task or assignment, without doing the assignment for them.</p>
<p>Visuals and non-verbal cues will be your best tool when assisting ELL (English Language Learners).</p>
<ul style="margin-left: 16px;">
  <li>🔑 Write key points or instructions on the board.</li>
  <li>📊 Use images, visual aids, diagrams, or props. If it&rsquo;s in the classroom, don&rsquo;t be afraid to use it!</li>
  <li>🙌🏿 Reinforce verbal directions with hand gestures or modeling.</li>
</ul>
<p><strong>Must do&rsquo;s when scaffolding lessons:</strong></p>
<ul style="margin-left: 16px;">
  <li>✅ Speak clearly.</li>
  <li>✅ Allow more wait time for answers.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 4
    {
      questions: [
        // Question 4.1
        {
          attribute: "the-sub-playbook.student-centered-instruction/collaboration",
          label: "Provide Opportunities for Collaboration and Conversation",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Let students practice those social skills in a way that promotes learning!</p>
<p>A lot of times lessons that are left behind when the teacher is away are quiet, worksheet-based instruction. Unfortunately, this isn&rsquo;t always conducive to an engaging day.</p>
<p>Structured talk keeps students engaged, with their thinking brains on!</p>
<ul style="margin-left: 16px;">
  <li>✅ Use &ldquo;turn and talk&rdquo; or think-pair-share activities.</li>
  <li>✅ Group students in pairs for simple tasks.</li>
  <li>✅ Encourage respectful discussion norms.</li>
</ul>
${tip({ text: "Remember to set clear expectations for group work to maintain classroom control." })}
`,
          },
        },
      ],
    },
    // Step 5
    {
      questions: [
        // Question 5.1
        {
          attribute: "the-sub-playbook.student-centered-instruction/choice",
          label: "Incorporate Student Choice",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>This is a great strategy if the students have been engaged and you feel in control of the day. Giving students options is a great way to ensure they feel in control of their learning.</p>
<ul style="margin-left: 16px;">
  <li>🪑 <strong>Offer flexible seating when possible:</strong> &ldquo;You can work at your desk, with a partner, or at the back table&mdash;just stay focused.&rdquo;</li>
  <li>🫱🏿‍🫲🏼 <strong>Allow them to work solo, in pairs, or small groups</strong> if it fits the task.</li>
  <li>👩🏻‍🏫 <strong>Allow them to share their work if they want:</strong> &ldquo;You can volunteer to share your answer out loud, write it on a sticky note for the board, or show me privately.&rdquo;</li>
  <li>🪞 <strong>Let students reflect on the tasks:</strong> Ask: &ldquo;What was the easiest part of this? What would you do differently next time?&rdquo; Let students choose how they respond (written, drawn, quick conversation).</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 6
    {
      questions: [
        // Question 6.1
        {
          attribute: "the-sub-playbook.student-centered-instruction/check-understanding",
          label: "Check for Understanding",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Student-Centered understanding checks look like:</p>
<ul style="margin-left: 16px;">
  <li>🃏 Using popsicle sticks, name cards, or name generators to call randomly. This is great if students have been eager to participate throughout the day.</li>
  <li>👍🏽 Asking for thumbs-up/down to gauge understanding. This is a great low-stakes way to check for understanding if the class has been quiet.</li>
  <li>🎟️ Use exit tickets or quick written checks to check for understanding if the class seems hesitant to speak aloud.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 7
    {
      questions: [
        // Question 7.1
        {
          attribute: "the-sub-playbook.student-centered-instruction/struggling-students",
          label: "Supporting Struggling Students",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>You would be surprised at how many students care what you think (even if you&rsquo;ve just met them for the day!) Students struggling academically or behaviorally often carry past experiences of frustration. How you respond to their frustrations, struggles, or comprehension of a subject can either help or harm their self-image.</p>
${tip({ text: "Celebrate the small wins and offer encouragement as they complete difficult tasks/assignments." })}
`,
          },
        },
      ],
    },
    // Step 8
    {
      questions: [
        // Question 8.1
        {
          attribute: "the-sub-playbook.student-centered-instruction/reflecting",
          label: "Reflecting on your Practice",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>When you are leading a room, remember that equity in the classroom starts with your own awareness of your actions.</p>
<p>Take time to reflect on whether your choices have been promoting and creating inclusive or exclusive environments based on your in-the-moment reactions, comments, or non-verbal signals throughout the day.</p>
`,
          },
        },
      ],
    },
    // Step 9
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 9.1
        {
          attribute: "the-sub-playbook.student-centered-instruction/practice-action",
          label: "Practice in Action",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>The situation:</strong></p>
<p>You are subbing for a 6th-grade Math class. The teacher left a two-page worksheet on long division. Looking around, you notice a very diverse group: some students are already finished in five minutes, some are staring blankly at the page (including two English Language Learners), and one student with an IEP is starting to look frustrated.</p>
<p><strong>Solution:</strong></p>
<p>Instead of just telling the class to &ldquo;get to work,&rdquo; you provide visual anchors. <strong>The Move:</strong> You walk to the board and write the steps of long division (Divide, Multiply, Subtract, Bring Down). Next to each word, you draw a small visual icon (e.g., a ÷ symbol, an arrow pointing down). You do the first problem together on the board, using hand gestures to show the &ldquo;bringing down&rdquo; of the numbers. You provide extra wait time after asking, &ldquo;What is our next step?&rdquo; to let everyone process the question.</p>
<p><strong>Another option</strong> is to implement structured talk. You ask the students who finished early to become &ldquo;Table Coaches.&rdquo; You explain to the class &ldquo;if you&apos;re finished, I want you to find a partner who is working on page one. Without giving them the answer, see if you can explain how you solved the &apos;remainder&apos; problem.&rdquo; This keeps the advanced students engaged and helps struggling students learn in a low-pressure way.</p>
<p>You continue to circulate the room during this time stopping to assist students as needed.</p>
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
